import { memo, useEffect, useState, useMemo } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
// Local files
import { Fields, Root, StyledField, Title } from './Settings.styled';
import Password from 'components/Dialogs/Password/Password';
import Email from 'components/Dialogs/Email/Email';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useProfile from 'hooks/useProfile';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import { nylasConnectUrl } from 'apis';
import useNylas from 'hooks/useNylas';
import { profileSelectors } from 'features/profile';
import { nylasSelectors } from 'features/nylas';

const Settings = () => {
  const { updateProfile } = useProfile();
  const { deleteNylas } = useNylas();
  const { setSuccess } = useSuccess();
  const fetchedProfile = useCustomSelector(profileSelectors.getProfileData);
  const isNylasConnected = fetchedProfile?.clinic?.nylas_account_id;
  const clinicToken = fetchedProfile?.clinic?.widget_token;
  const profileFetching = useCustomSelector(profileSelectors.profileIsFetching);
  const nylasFetching = useCustomSelector(nylasSelectors.nylasIsFetching);
  const [passwordDialog, setPasswordDialog] = useState({ open: false });
  const [emailDialog, setEmailDialog] = useState({ open: false });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#08A0B7');
  const [secondaryColor, setSecondaryColor] = useState('#bc7bd5');
  const [color, setColor] = useState('#ffffff');
  const [branding, setBranding] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [showAvatar, setShowAvatar] = useState(false);
  const processing = profileFetching || nylasFetching;

  const widgetCode = useMemo(() => `<iframe\n  src='https://${window.location.hostname === 'localhost' || window.location.hostname === 'clinic-staging.converse.health' ? 'widget-staging' : 'widget'}.converse.health/?client_token=${clinicToken}&primary_color=${primaryColor.substring(1)}&secondary_color=${secondaryColor.substring(1)}&color=${color.substring(1)}&avatar=${showAvatar}&branding=${branding}&mode=${darkMode}'\n  title='Converse Health'\n  style='border: 0; position: fixed; height: min(500px, 100vh - 32px); width: min(360px, 100vw - 40px); bottom: 0px; right: 0px'\n/>`
  , [clinicToken, primaryColor, secondaryColor, color, showAvatar, branding, darkMode]);

  const needToUpdate = (a,b) => a.first_name !== b.first_name || a.last_name !== b.last_name || a.email !== b.email || !!b?.password;
  const handleChange = (email = null, password = null) => {
    const manager = { 
      first_name: firstName || null, 
      last_name: lastName || null, 
      email: email || fetchedProfile.email, 
      ...(!!password && { password }) 
    };

    if (needToUpdate(fetchedProfile, manager)) {
      updateProfile(manager)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Manager successfully updated');
          handleClose();
        }
      })
    } else {  
      handleClose();
    }
  };
  const handleClose = () => {
    setPasswordDialog({ open: false });
    setEmailDialog({ open: false });
  };
  const handleNylasClick = () => {
    if (isNylasConnected) {
      deleteNylas()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Mailbox successfully disconnected');
        }
      })
    } else {
      window.open(`${nylasConnectUrl}&redirect_uri=${window.location.origin}/nylas_callback&response_type=code`, '_self');
    }
  };
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(widgetCode);
    setSuccess('Copied');
  };

  useEffect(() => {
    setFirstName(fetchedProfile.first_name);
    setLastName(fetchedProfile.last_name);
  }, [fetchedProfile]);

  return (
    <Root>
      <Password {...passwordDialog} onChange={handleChange} onClose={() => setPasswordDialog({ open: false })} />
      <Email {...emailDialog} onChange={handleChange} onClose={() => setEmailDialog({ open: false })} />
      <Container maxWidth='sm' >
        <Card sx={{ p: 2, mb: 4 }}>
          <Title>Main</Title>
          <Fields>
            <StyledField
              label='First name'
              disabled={processing}
              value={firstName}
              onChange={({ target: { value } }) => setFirstName(value)}
            />
            <StyledField
              label='Last name'
              disabled={processing}
              value={lastName}
              onChange={({ target: { value } }) => setLastName(value)}
            />
          </Fields>
          <Button variant='contained' sx={{ display: 'flex', px: 5 }} disabled={processing} onClick={() => handleChange()}>Save</Button>
        </Card>
        <Card sx={{ p: 2, mb: 4 }}>
          <Title>Access</Title>
          <Fields>
            <StyledField
              label='Email'
              value={fetchedProfile.email}
              InputProps={{ readOnly: true }}
            />
            <Button variant='outlined' sx={{ px: 2 }} disabled={processing} onClick={() => setEmailDialog({ open: true })}>Change email</Button>
          </Fields>
          <Button 
            sx={{ mb: 2 }} 
            variant='outlined' 
            onClick={handleNylasClick}
          >
            {isNylasConnected ? 'Disconnect' : 'Connect'} mailbox
          </Button> 
          <Typography variant='body2' mb={1}>If you want to change your password, open the form:</Typography>
          <Button variant='outlined' sx={{ display: 'flex', px: 2 }} disabled={processing} onClick={() => setPasswordDialog({ open: true })}>Change current password</Button>
        </Card>
        <Card sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Title sx={{ mb: 0 }}>Widget integration</Title>
            <Button variant='outlined' disabled={processing} onClick={handleCopyUrl}>Copy widget code</Button>
          </Box>
          <Box sx={{ display: 'flex', p: 1, gap: 8, alignItems: 'center' }}>
            <Box>
              <Box sx={{ fontWeight: 600 }}>Primary color</Box>
              <input type='color' value={primaryColor} onChange={({ target: { value } }) => setPrimaryColor(value) } />
              <Box sx={{ fontWeight: 600, mt: 1 }}>Secondary color</Box>
              <input type='color' value={secondaryColor} onChange={({ target: { value } }) => setSecondaryColor(value) } />
              <Box sx={{ fontWeight: 600, mt: 1 }}>Header text color</Box>
              <input type='color' value={color} onChange={({ target: { value } }) => setColor(value) } />
            </Box>
            <Box>
              <Box sx={{ fontWeight: 600, mt: 1 }}>Branding</Box>
              <input type='checkbox' checked={branding} onChange={() => setBranding(!branding) } />
              <Box sx={{ fontWeight: 600, mt: 1 }}>Dark theme</Box>
              <input type='checkbox' checked={darkMode} onChange={() => setDarkMode(!darkMode)} />
              <Box sx={{ fontWeight: 600, mt: 1 }}>Avatar</Box>
              <input type='checkbox' checked={showAvatar} onChange={() => setShowAvatar(!showAvatar) } />
            </Box>
          </Box>
          <Box sx={{ fontSize: '1.25rem', fontWeight: 600, p: 1, mt: 2 }}>Widget code:</Box>
          <TextField
            fullWidth
            inputProps={{ readOnly: true }}
            multiline
            value={widgetCode}
            sx={{ fontStyle: 'italic', wordBreak: 'break-all' }}
          />
        </Card>
      </Container>
    </Root>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <Settings {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);